import CryptoJS from "crypto-js";

export const generate12DigitId = (inputString: string) => {
  console.log("Raw device id:", inputString);
  try {
    const hash = CryptoJS.SHA256(inputString).toString(CryptoJS.enc.Hex);
    const shortHash = hash.substring(0, 12).toUpperCase();
    return (
      shortHash.substring(0, 2) + ":" +
      shortHash.substring(2, 4) + ":" +
      shortHash.substring(4, 6) + ":" +
      shortHash.substring(6, 8) + ":" +
      shortHash.substring(8, 10) + ":" +
      shortHash.substring(10, 12)
    );
  } catch (error) {
    console.error("Error generating ID:", error);
    return "NO:ID:FO:UN:DE:D";
  }
};

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const getAndroidIdWithRetry = async (retries = 20, delayMs = 100): Promise<string> => {
  for (let i = 0; i < retries; i++) {
    const id = window.AndroidBridge?.getAndroidId?.() || "";
    if (id && String(id).trim() !== "") {
      // Cache so sync callers (e.g. ad params) can read the last-known id
      // without awaiting the native bridge retry loop again.
      localStorage.setItem("android_id", id);
      return id;
    }
    await wait(delayMs);
  }
  return "";
};

export interface DeviceIdentity {
  /** Always the XX:XX:XX:XX:XX:XX hashed format — use for device auth */
  deviceId: string;
  /** GAID / advertising UUID when available — use for ad targeting */
  advertisingId: string;
  wifiMac: string;
}

// Device/advertising IDs don't change during a session, but fetching them hits
// native bridge calls (including a blocking GMS IPC round-trip for the ad ID)
// that can be slow on some Android TV boxes. Cache the result so repeated
// callers (e.g. a channel switch on every zap) don't pay that cost again.
let cachedIdentity: Promise<DeviceIdentity> | null = null;

export const getDeviceIdentity = async (): Promise<DeviceIdentity> => {
  if (cachedIdentity) return cachedIdentity;
  cachedIdentity = fetchDeviceIdentity().catch((error) => {
    cachedIdentity = null; // allow retry on next call if this attempt failed
    throw error;
  });
  return cachedIdentity;
};

const fetchDeviceIdentity = async (): Promise<DeviceIdentity> => {
  const isAndroid = !!window.AndroidBridge?.getAndroidId;

  try {
    if (isAndroid) {
      const rawAndroidId = await getAndroidIdWithRetry();
      const wifiMac = window.AndroidBridge?.getMacAddress?.() || "";
      const gaid = window.AndroidBridge?.getAdvertisingId?.() || "";
      if (gaid) localStorage.setItem("gaid", gaid);

      if (!rawAndroidId) {
        throw new Error("Android ID not available (bridge not ready).");
      }

      const formattedId = generate12DigitId(rawAndroidId);
      localStorage.setItem("device_id_local", formattedId);
      return { deviceId: formattedId, advertisingId: gaid, wifiMac };
    }

    const stored = localStorage.getItem("generated_device_id");
    if (stored) return { deviceId: stored, advertisingId: "", wifiMac: "" };
    const randomId = generate12DigitId(Math.random().toString());
    localStorage.setItem("generated_device_id", randomId);
    return { deviceId: randomId, advertisingId: "", wifiMac: "" };
  } catch (error) {
    console.error("Error getting device identity:", error);
    throw error;
  }
};
